// ระบบประเมินพนักงาน — shared constants, the assignment-grid builder, and a client-side
// mirror of the scoring maths.
//
// On that mirror: the AUTHORITATIVE score is always the one Xano writes
// (score_evaluation_task). What is here is only used to (a) show a live "คะแนนที่จะได้"
// while someone fills a form in, and (b) render the per-question breakdown in the report,
// which Xano deliberately does not store — the task keeps the evaluator's raw answers as a
// pristine audit trail and nothing else. Keep the two definitions in step: normalise every
// question to 0..1, weight it, and drop N/A/unanswered from BOTH sides of the division.

export const RELATIONS = [
  { value: 'peer',                 label: 'เพื่อนร่วมงาน',        short: 'เพื่อนร่วมงาน', anonymous: true },
  { value: 'manager',              label: 'ผู้จัดการสาขา',        short: 'ผู้จัดการ' },
  { value: 'cross_branch_manager', label: 'ผู้จัดการสาขาอื่น',    short: 'ผจก.สาขาอื่น' },
  { value: 'general_manager',      label: 'ส่วนกลาง (GM)',        short: 'ส่วนกลาง' },
  { value: 'doctor',               label: 'ทันตแพทย์',            short: 'ทันตแพทย์' },
  { value: 'custom',               label: 'กำหนดเอง',             short: 'กำหนดเอง' },
]

export const relationLabel = (v) => RELATIONS.find((r) => r.value === v)?.label || v
export const relationShort = (v) => RELATIONS.find((r) => r.value === v)?.short || v
export const isAnonymousRelation = (v) => v === 'peer'

// Scale is fixed at 1-5 and not configurable. One scale across every form keeps scores
// comparable between positions and rounds, and removes the commonest way to make a
// questionnaire meaningless (a 1-10 here, a 1-3 there). The min/max are still stored on the
// question and still read by the scorer, so older forms and any future change stay valid —
// the builder just does not offer the knob.
export const SCALE_MIN = 1
export const SCALE_MAX = 5

export const QUESTION_TYPES = [
  { value: 'scale',    label: 'Scale',      hint: 'ให้คะแนน 1-5' },
  { value: 'dropdown', label: 'ตัวเลือก',    hint: 'เลือกหนึ่งข้อ แต่ละข้อมีคะแนน 0-100' },
  { value: 'yesno',    label: 'ใช่ / ไม่ใช่', hint: 'ตอบใช่ได้เต็ม ไม่ใช่ได้ศูนย์' },
  { value: 'text',     label: 'ข้อความ',     hint: 'ไม่คิดคะแนน เก็บเป็นความเห็น' },
]

export const CYCLE_TYPES = ['ประจำปี', 'ทดลองงาน', 'เฉพาะกิจ']

export const DECISIONS = [
  '-', 'ผ่านทดลองงาน', 'ต่อทดลองงาน', 'ไม่ผ่าน', 'โบนัส A', 'โบนัส B', 'โบนัส C',
]

// Positions that a Manager may never see as an evaluation SUBJECT. Mirrors the filter in
// the Xano stack (GET /evaluation/result) — this copy only shapes the UI.
export const MANAGER_LEVEL_POSITIONS = new Set(['ผู้จัดการ', 'CEO'])

/* ── scoring (mirror of Xano score_evaluation_task) ───────────────────────── */

// One answer → 0..1, or null when it does not count toward the score at all.
export function answerScore(question, answer) {
  if (!question || question.type === 'text') return null
  if (!answer || answer.na === true) return null
  const v = answer.value
  if (v == null || v === '') return null

  if (question.type === 'scale') {
    const lo = Number(question.scale?.min ?? SCALE_MIN)
    const hi = Number(question.scale?.max ?? SCALE_MAX)
    if (!(hi > lo)) return 0
    return (Number(v) - lo) / (hi - lo)
  }
  if (question.type === 'dropdown') {
    const opt = (question.options || []).find((o) => o.value === v)
    // Option scores are authored on a fixed 0-100 scale, which is what lets the server skip
    // any max-of-options maths. Keep it that way on both sides.
    return Math.max(0, Math.min(1, Number(opt?.score ?? 0) / 100))
  }
  // yesno — compare as text. `"no" == true` is TRUE in XanoScript, and this mirror exists to
  // agree with the server, so both sides normalise to a string before testing.
  const s = String(v).toLowerCase()
  return (s === 'yes' || s === 'true' || s === '1' || s === 'ใช่') ? 1 : 0
}

// Whole paper → percent, or null when nothing scoreable was answered.
export function taskScorePercent(questions, answers) {
  const list = Array.isArray(questions) ? questions : []
  const byId = new Map((answers || []).map((a) => [a.question_id, a]))
  let num = 0
  let den = 0
  for (const q of list) {
    const s = answerScore(q, byId.get(q.id))
    if (s == null) continue
    const w = Number(q.weight ?? 1)
    num += s * w
    den += w
  }
  return den > 0 ? (num * 100) / den : null
}

// Which required questions are still unanswered — the same check the submit endpoint makes,
// surfaced early so the button can explain itself instead of failing.
export function missingRequired(questions, answers) {
  const byId = new Map((answers || []).map((a) => [a.question_id, a]))
  return (questions || []).filter((q) => {
    if (!q.required) return false
    const a = byId.get(q.id)
    if (!a) return true
    if (a.na === true) return false
    return a.value == null || a.value === ''
  })
}

/* ── the assignment grid ──────────────────────────────────────────────────── */

// Build the proposed evaluator grid entirely from data the client already holds.
//
// Only the `doctor` column needs the server, because "which doctors actually worked at this
// branch during the period" lives in `appointment` — everything else is a filter over the
// employee roster the page has already fetched. That split is why there is no heavyweight
// /proposal endpoint: the grid stays instant to edit, and dispatch validates what it is
// finally handed.
//
//   subjects   : [Employee]        the people being evaluated
//   rules      : [evaluation_rule] the whole active matrix
//   employees  : [Employee]        the active roster
//   doctorsByBranch : { [branch_id]: [{doctor_id, name, nickname, shifts}] }
//
// → [{ subject, rows: [{ rule, relation, candidates:[{kind,id,label,hint}], selected:Set }] }]
export function buildGrid({ subjects, rules, employees, doctorsByBranch = {} }) {
  const active = (employees || []).filter((e) => e.active !== false)

  // Chips and pickers are labelled with `code` — the identifier staff actually use for each
  // other ("เอก ธนิตย์" = nickname + first name, the same convention on Employee and Doctor).
  // A bare nickname is not unique: the roster has several เอก and several อุ้ม, and a grid of
  // ambiguous chips is one that gets mis-ticked. Fall back to nickname/name only when a
  // record has no code.
  const empChip = (e) => ({
    kind: 'employee',
    id: e.id,
    label: e.code || e.nickname || e.name || `#${e.id}`,
    hint: e.position || '',
    // What to bucket this candidate under when the row is rendered grouped (peer).
    group: e.position || 'ไม่ระบุตำแหน่ง',
  })
  const docChip = (d) => ({
    kind: 'doctor',
    id: d.doctor_id,
    label: d.code || d.nickname || d.name || `#${d.doctor_id}`,
    hint: d.shifts ? `${d.shifts} เวร` : 'ทันตแพทย์',
    group: 'ทันตแพทย์',
  })

  return (subjects || []).map((subject) => {
    const mine = (rules || []).filter(
      (r) => r.active !== false && r.subject_position === subject.position,
    )

    const rows = mine.map((rule) => {
      let candidates = []
      switch (rule.relation) {
        case 'peer': {
          const want = new Set(rule.peer_positions || [])
          candidates = active
            .filter((e) => e.id !== subject.id
              && e.branch_id === subject.branch_id
              && (want.size === 0 || want.has(e.position)))
            .map(empChip)
          break
        }
        case 'manager':
          candidates = active
            .filter((e) => e.id !== subject.id
              && e.branch_id === subject.branch_id
              && e.position === 'ผู้จัดการ')
            .map(empChip)
          break
        case 'cross_branch_manager':
          // "ผู้จัดการด้วยกัน" — deliberately EXCLUDES the GM, who evaluates the same
          // subject through the general_manager row on a different form and weight.
          candidates = active
            .filter((e) => e.id !== subject.id
              && e.branch_id !== subject.branch_id
              && e.position === 'ผู้จัดการ'
              && !isGM(e))
            .map(empChip)
          break
        case 'general_manager':
          candidates = active
            .filter((e) => e.id !== subject.id && isGM(e))
            .map(empChip)
          break
        case 'doctor':
          candidates = (doctorsByBranch[subject.branch_id] || []).map(docChip)
          break
        default:
          // `custom` proposes nobody on purpose — a back-office rule is hand-picked.
          candidates = []
      }
      return {
        rule,
        relation: rule.relation,
        candidates,
        // Nothing is pre-ticked. `candidates` is who COULD evaluate this person, which for
        // the doctor row is every dentist who worked the branch — dozens of people. Ticking
        // them all by default turns one careless click into ~70 questionnaires nobody meant
        // to send, and makes "review the grid" a deletion exercise. Choosing is the point.
        selected: new Set(),
      }
    })

    return { subject, rows }
  })
}

// Which row a hand-picked ("กำหนดเอง") evaluator should join.
//
// Deliberately NOT a group of its own. A custom group would need its own question set and
// its own weight, and there is no honest default for either: borrowing the peer weight would
// count that share twice, and inventing a new one changes what the total means. Someone you
// add by hand is in substance a colleague, so they take the peer row — same questionnaire as
// the other peers, pooled into the same average, no new weight. Positions with no peer rule
// fall back to their first rule so the feature still works.
export function customTargetIndex(rows) {
  const peer = (rows || []).findIndex((r) => r.relation === 'peer')
  if (peer >= 0) return peer
  return (rows || []).length ? 0 : -1
}

export const chipKey = (c) => `${c.kind}:${c.id}`

// GM is an employee-table ROLE, not a position — a GM is usually also a branch
// ผู้จัดการ. Same normalisation as lib/role.js so "GM" / "gm" / "g m" all match.
const normRole = (r) => String(r || '').toLowerCase().replace(/[\s._-]+/g, '')
const isGM = (e) => (Array.isArray(e?.role) ? e.role : [e?.role]).some((r) => normRole(r) === 'gm')

// Grid → the `assignments` body POST /evaluation/cycle/:id/dispatch expects.
export function gridToAssignments(grid) {
  const out = []
  for (const { subject, rows } of grid || []) {
    for (const row of rows) {
      // No rule = an unconfigured กำหนดเอง row. Nothing to send it on.
      if (!row.rule) continue
      const evaluators = row.candidates
        .filter((c) => row.selected.has(chipKey(c)))
        .map((c) => ({ kind: c.kind, id: c.id }))
      if (!evaluators.length) continue
      out.push({
        subject_employee_id: subject.id,
        relation: row.relation,
        rule_id: row.rule.id,
        evaluators,
      })
    }
  }
  return out
}

// How many questionnaires the grid will actually send, and to how many distinct people.
export function gridTotals(grid) {
  let papers = 0
  const people = new Set()
  for (const { rows } of grid || []) {
    for (const row of rows) {
      for (const c of row.candidates) {
        if (!row.selected.has(chipKey(c))) continue
        papers += 1
        people.add(chipKey(c))
      }
    }
  }
  return { papers, people: people.size }
}

// Cells that fall below the rule's min_evaluators, so the reviewer can be warned before
// dispatching a round that cannot produce a meaningful average.
export function gridWarnings(grid) {
  const warn = []
  for (const { subject, rows } of grid || []) {
    for (const row of rows) {
      // กำหนดเอง has no minimum — it is optional by nature, and an unconfigured one has no
      // rule to read a minimum from anyway.
      if (!row.rule || row.relation === 'custom') continue
      const n = row.candidates.filter((c) => row.selected.has(chipKey(c))).length
      const min = Number(row.rule.min_evaluators ?? 1)
      if (n < min) {
        warn.push({ subject, relation: row.relation, have: n, need: min })
      }
    }
  }
  return warn
}

/* ── display helpers ──────────────────────────────────────────────────────── */

export function fmtPercent(v, digits = 1) {
  if (v == null || v === '') return '—'
  const n = Number(v)
  return Number.isFinite(n) ? `${n.toFixed(digits)}%` : '—'
}

// Score → pill tone. Bands are a display convention only; the pass mark that matters is
// whatever HR writes into `decision`.
export function scoreTone(v) {
  if (v == null) return 'slate'
  const n = Number(v)
  if (n >= 80) return 'emerald'
  if (n >= 60) return 'amber'
  return 'rose'
}

export const TONE_CLASS = {
  emerald: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  amber:   'bg-amber-50 text-amber-700 ring-amber-200',
  rose:    'bg-rose-50 text-rose-700 ring-rose-200',
  slate:   'bg-slate-100 text-slate-500 ring-slate-200',
  violet:  'bg-violet-50 text-violet-700 ring-violet-200',
  sky:     'bg-sky-50 text-sky-700 ring-sky-200',
}

/* ── folding several rounds into one line ─────────────────────────────────── */

// Fold the group_scores of SEVERAL rounds into one set, for the employee-centric report
// where a filter can select more than one round per person.
//
// Each group is averaged WEIGHTED BY n_submitted, not by round: a relation where three
// colleagues answered in one round and one in the next is 3:1, exactly as it would have been
// had all four sat in the same round. Averaging the two round-averages instead would let a
// single late reply outvote three earlier ones.
//
// A round that returned nothing for a relation (n_submitted 0) contributes only to n_expected,
// so it still shows as outstanding without dragging the average toward zero.
//
// `rounds` must arrive NEWEST FIRST: weight_percent is taken from the first round that
// carries the relation, because เกณฑ์ can be re-weighted between rounds and the current
// weighting is the one worth reporting.
export function aggregateGroupScores(rounds) {
  const acc = new Map()
  for (const gs of rounds || []) {
    for (const g of gs || []) {
      const key = g?.relation
      if (!key) continue
      if (!acc.has(key)) {
        acc.set(key, {
          relation: key, weight_percent: 0, n_expected: 0, n_submitted: 0, _weighted: 0,
        })
      }
      const a = acc.get(key)
      const n = Number(g.n_submitted) || 0
      a.n_expected += Number(g.n_expected) || 0
      a.n_submitted += n
      if (!a.weight_percent) a.weight_percent = Number(g.weight_percent) || 0
      if (g.avg_percent != null && n > 0) a._weighted += Number(g.avg_percent) * n
    }
  }
  return [...acc.values()].map(({ _weighted, ...g }) => ({
    ...g,
    avg_percent: g.n_submitted > 0 ? _weighted / g.n_submitted : null,
  }))
}

// Groups → one total. Mirrors Xano's roll-up exactly: SUM(avg × weight) / SUM(weight) over
// the groups that actually returned something, so a group nobody answered is DROPPED and the
// rest renormalised rather than counted as zero. A person whose ทันตแพทย์ block never replied
// is not scored as though every dentist gave them 0.
export function totalFromGroups(groups) {
  let num = 0
  let den = 0
  for (const g of groups || []) {
    if (g?.avg_percent == null) continue
    if (!(Number(g.n_submitted) > 0)) continue
    const w = Number(g.weight_percent) || 0
    num += Number(g.avg_percent) * w
    den += w
  }
  return den > 0 ? num / den : null
}

// Progress across every group of one result row.
export function resultProgress(row) {
  const g = row?.group_scores || []
  const expected = g.reduce((s, x) => s + Number(x.n_expected || 0), 0)
  const submitted = g.reduce((s, x) => s + Number(x.n_submitted || 0), 0)
  return { expected, submitted, complete: expected > 0 && submitted >= expected }
}

// A blank question for the builder. `id` is a canonical token and NEVER the Thai label —
// answers are stored against it, so rewording a question must not orphan them.
export function blankQuestion(index) {
  return {
    id: `q${index + 1}_${Math.random().toString(36).slice(2, 7)}`,
    type: 'scale',
    label: '',
    required: true,
    weight: 1,
    allow_na: false,
    scale: { min: SCALE_MIN, max: SCALE_MAX },
    options: [],
  }
}

// Every question is required. Enforced on save rather than left to a per-question toggle:
// an optional question in a staff evaluation is one that quietly gets skipped, and a score
// averaged over "whichever questions this evaluator felt like answering" is not comparable
// between two people. Where an evaluator genuinely has not seen something, allow_na gives
// them an explicit ไม่มีข้อมูล — recorded, and excluded from both sides of the score —
// which is a different statement from silence.
export function normalizeQuestions(questions) {
  return (Array.isArray(questions) ? questions : []).map((q) => ({ ...q, required: true }))
}
